import { Component, EventEmitter, Input, OnChanges, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { SupportingDocument, SupportingDocumentSection } from './models/SupportingDocumentSection.model';
import { UpdateExistingSupportingDocumentDescriptionEvent } from './models/UpdateExistingSupportingDocumentDescriptionEvent.model';
import { SupportingDocumentSectionComponent } from './components/supporting-document-section/supporting-document-section.component';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { ReferenceService } from '../../../../../shared/reference/reference.service';
import { ImageUtility } from '../../../../../utilities/ImageUtility';
import { DraftSupportingDocumentDialogComponent } from './components/draft-supporting-document-dialog/draft-supporting-document-dialog.component';

@Component({
  selector: 'app-supporting-documents-step',
  templateUrl: './supporting-documents-step.component.html',
  styleUrls: ['./supporting-documents-step.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatTableModule,
    MatPaginator,
    MatExpansionModule,
    MatButtonModule,
    SupportingDocumentSectionComponent
  ]
})
export class SupportingDocumentsStepComponent implements OnChanges {

  damageClaimType = 'claim_cause_damage_rental_property';
  theftClaimType = 'claim_cause_theft_fire';
  fireClaimType = 'claim_cause_theft_fire';
  bodilyInjuryClaimType = 'claim_cause_injury';
  otherClaimType = 'claim_cause_other';

  @Input('lossForm') lossForm!: FormGroup;
  @Input() supportingDocumentSections!: SupportingDocumentSection[];
  @Input() existingSupportingDocuments$!: BehaviorSubject<SupportingDocument[]>;
  @Input() invalidSupportingDocumentNames!: string[];
  
  @Output() supportingDocumentSectionsChange = new EventEmitter<SupportingDocumentSection[]>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChildren('imageRef') imageRefs!: QueryList<any>;

  dataSource: MatTableDataSource<SupportingDocument> = new MatTableDataSource<SupportingDocument>();
  displayedColumns: string[] = ['image', 'name', 'description', 'actions'];
  readonly pageSizeOptions = [10];

  private readonly processedDocumentIds = new Set<string>();
  private readonly pageSize = 10;

  title: string = "Supporting Documents";
  complete: boolean = false;
  touched: boolean = false;
  hasExistingDocuments = false;
  panelOpen: boolean = false;
  icon: string = "expand_more";

  constructor(
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    public imageUtility: ImageUtility,
    private referenceService: ReferenceService,
    private logger: Logger
  ) { }

  ngOnChanges() {
    this.complete = this.hasSupportingDocument() && this.supportingDocumentSections.every(section => section.description?.length > 0);
  }

  ngOnInit() {
    this.existingSupportingDocuments$.subscribe(documents => {
      if (documents.length > 0) {
        this.hasExistingDocuments = true;
        this.touched = true;
      }

      documents.sort((a, b) => a.id > b.id ? 1 : -1);
      
      this.dataSource = new MatTableDataSource(documents);
      this.dataSource.paginator = this.paginator;

      documents
        .filter(document => {
          return this.imageUtility.isImage(document.fileName) && 
            document.fileData == null &&
            !this.processedDocumentIds.has(document.id);
        })
        .forEach(async document => this.loadDocumentImage(document));
    });
  }

  private async loadDocumentImage(document: SupportingDocument) {
    try {
      this.processedDocumentIds.add(document.id);

      const imageData = await this.referenceService.getFileDataById(document.id);
      if (imageData == null) {
        return;
      }

      const image = await this.imageUtility.getFileFromBlob(imageData.image);
      if (image == null) {
        return;
      }
      document.fileData = image;
    } catch (e) {
      this.logger.handleError(e, 'An error occurred while loading image', 'Error while loading image');
    }
  }
  
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  isStepComplete() {
    return this.hasSupportingDocument() && 
      this.supportingDocumentSections.every(section => section.description?.length > 0 && section.documents?.length > 0);
  }

  hasSupportingDocument() {
    return this.hasExistingDocuments || this.supportingDocumentSections?.some(section => section.documents.length > 0); 
  }

  displayUploadDocumentsView() {
    return this.getFormValue(this.damageClaimType)
      || this.getFormValue(this.theftClaimType)
      || this.getFormValue(this.fireClaimType)
      || this.getFormValue(this.bodilyInjuryClaimType)
      || this.getFormValue(this.otherClaimType);
  }

  getFormValue(formControlName: string): boolean {
    return this.lossForm.controls[formControlName].value;
  }

  onSectionUpdated() {
    if (!this.touched) {
      this.touched = true;
    }

    this.supportingDocumentSectionsChange.emit(this.supportingDocumentSections);

    this.complete = this.hasSupportingDocument();
  }

  onDeleteSection(sectionId: string) {
    if (!this.touched) {
      this.touched = true;
    }
    const sectionIndex = this.supportingDocumentSections.findIndex(s => s.id == sectionId);

    if (sectionIndex !== -1) {
      const updatedSections = [...this.supportingDocumentSections];
      updatedSections.splice(sectionIndex, 1);
      this.supportingDocumentSections = updatedSections;
      
      this.supportingDocumentSectionsChange.emit(this.supportingDocumentSections);
    }
    
    this.complete = this.hasSupportingDocument();
  }

  onDeleteExistingDocument(documentId: string) {
    if(!this.touched) {
      this.touched = true;
    }

    const documentIndex = this.existingSupportingDocuments$.value.findIndex(document => document.id == documentId);
    if (documentIndex !== -1) {
      const documents = this.existingSupportingDocuments$.value;//.splice(documentIndex, 1);
      documents.splice(documentIndex, 1);
      this.existingSupportingDocuments$.next(documents);

      this.snackbar.open('Deleted', 'OK', { duration: 3000 });
    }

    this.complete = this.hasSupportingDocument();
  }

  onUpdateExistingDocumentDescription(event: UpdateExistingSupportingDocumentDescriptionEvent) {
    if (!this.touched) {
      this.touched = true;
    }

    const documentIndex = this.existingSupportingDocuments$.value.findIndex(document => document.id == event.documentId);
    if (documentIndex !== -1) {
      this.existingSupportingDocuments$.value[documentIndex].description = event.description;

      this.snackbar.open('Saved', 'OK', { duration: 3000 });
    }
  }

  canAddSection(): boolean {
    for(let i = 0; i < this.supportingDocumentSections.length; i++){
      if(this.supportingDocumentSections[i].documents.length === 0) {
        return false;
      }
    }
    return true;
  }

  addSection() {
    if (!this.touched) {
      this.touched = true;
    }

    if (this.canAddSection()) {
      this.supportingDocumentSections.push({
        id: uuid(),
        description: '',
        documents: []
      });
      this.supportingDocumentSections = [...this.supportingDocumentSections];
    }
  }

  onEditClick(document: SupportingDocument) {
    const documentExtension = document.fileName.split('.')[1];
    const isImage = this.imageUtility.isImageFileType(documentExtension);

    const dialogRef = this.dialog.open(DraftSupportingDocumentDialogComponent, {
      width: '80vw',
      maxWidth: isImage ? '1000px' : '600px',
      data: {
        isImage: isImage,
        document: document
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      switch(result?.action) {
        case 'delete':
          this.onDeleteExistingDocument(result.id);
          break;
        case 'description':
          this.onUpdateExistingDocumentDescription({ documentId: result.id, description: result.description });
          break;
        case 'close':
          break;
      }
    });
  }
}